/**
 * i18n resources for the Operations (in-stay tasks) feature slice.
 *
 * Namespace: `operations`. Exposed as `{ namespace, resources: { en, bg, de, fr } }`
 * so the app shell can merge it into the shared i18next instance and tests can
 * pass it straight to `renderWithProviders({ i18nResources })`.
 *
 * Key parity is enforced by `@hostesa/i18n` (a missing key THROWS in test +
 * production), so every locale below carries the same key set. Non-`en` locales
 * are seeded as English placeholders where a real translation is not yet
 * available — the documented temporary state.
 */

const en = {
  eyebrow: 'Operations',
  board: {
    title: 'Tasks',
    description:
      'An exceptions-first view of in-stay service requests. A near-empty board is a healthy shift.',
    handledToday: '{{count}} handled today',
    showAll: 'Show all tasks',
    showExceptions: 'Show exceptions only',
    routingLink: 'Routing rules',
  },
  exceptions: {
    title: 'Needs attention',
    description: 'Requests that are unacknowledged, overdue, or escalated.',
    empty: 'Nothing needs your attention right now.',
    emptyDescription: 'Healthy requests are routed straight to the team and stay off this board.',
  },
  columns: {
    needsAttention: 'Needs attention',
    inProgress: 'In progress',
    overdue: 'Overdue',
    doneToday: 'Done today',
  },
  status: {
    new: 'New',
    assigned: 'Assigned',
    in_progress: 'In progress',
    done: 'Done',
    cancelled: 'Cancelled',
    escalated: 'Escalated',
  },
  urgency: {
    label: 'Urgency',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
  },
  category: {
    housekeeping: 'Housekeeping',
    maintenance: 'Maintenance',
    food_beverage: 'Food & beverage',
    concierge: 'Concierge',
    reception: 'Reception',
    other: 'Other',
  },
  card: {
    room: 'Room',
    roomUnconfirmed: 'Room unconfirmed',
    team: 'Team',
    unassigned: 'Unassigned',
    sla: 'SLA',
    source: 'Source',
    escalatedNote: 'Escalated — awaiting human resolution',
  },
  source: {
    voice: 'Voice',
    chat: 'Chat',
    whatsapp: 'WhatsApp',
    email: 'Email',
    manual: 'Manual',
  },
  actions: {
    markInProgress: 'Start',
    markDone: 'Mark done',
    cancel: 'Cancel',
    escalate: 'Escalate',
    updating: 'Updating…',
    escalating: 'Escalating…',
  },
  escalate: {
    title: 'Escalate this request',
    description:
      'Escalation hands the request to a human and re-routes it. This cannot be undone automatically, so it is confirmed with the backend before the board updates.',
    reasonLabel: 'Reason (optional)',
    reasonPlaceholder: 'What needs a human decision?',
    confirm: 'Escalate request',
    cancel: 'Keep on board',
    error: 'The request could not be escalated. Nothing was changed — please try again.',
  },
  states: {
    loadingTitle: 'Loading tasks',
    errorTitle: 'Could not load tasks',
    errorDescription: 'Something went wrong loading the tasks board. Please try again.',
    forbiddenTitle: 'No access to operations',
    forbiddenDescription: 'You do not have permission to view service requests for this hotel.',
    disabledTitle: 'In-stay operations is off',
    disabledDescription:
      'The in-stay operations agent is not enabled for this hotel. Enable the feature to start capturing service requests.',
  },
  routing: {
    title: 'Routing rules',
    description:
      'Configure how captured requests reach the right team — category, notification target, and SLA.',
    eyebrow: 'Operations',
    emptyTitle: 'Routing is configured by Hostesa',
    emptyDescription:
      'There is no self-serve routing editor yet. Your housekeeping target and SLA are set up with your onboarding contact. The board below reflects how requests currently route.',
    categoryHeading: 'Category',
    teamHeading: 'Team',
    slaAckHeading: 'SLA to acknowledge',
    slaDoneHeading: 'SLA to complete',
    activeBadge: 'Active',
    plannedBadge: 'Planned',
    housekeepingNote: 'Only housekeeping is routed in this version.',
  },
};

type OperationsResources = typeof en;

// Non-English locales reuse the English copy until translated (key parity).
const bg: OperationsResources = en;
const de: OperationsResources = en;
const fr: OperationsResources = en;

export const namespace = 'operations' as const;

export const resources: Record<'en' | 'bg' | 'de' | 'fr', { operations: OperationsResources }> = {
  en: { operations: en },
  bg: { operations: bg },
  de: { operations: de },
  fr: { operations: fr },
};

export const operationsI18n = { namespace, resources } as const;
